import { useCallback, useEffect, useRef, useState } from "react";

import { SEARCH_DEBOUNCE_MS } from "../constants";
import { useFilterContext } from "../context/FilterContext";

export interface UseSearchFilterOptions {
  queryKey: string;
}

export interface UseSearchFilterResult {
  inputValue: string;
  debouncedValue: string;
  onChange: (value: string) => void;
}

export const useSearchFilter = ({
  queryKey,
}: UseSearchFilterOptions): UseSearchFilterResult => {
  const { filterState, setFilter, removeFilter } = useFilterContext();
  const contextValue = filterState[queryKey] ?? "";

  const [inputValue, setInputValue] = useState(contextValue);
  const [debouncedValue, setDebouncedValue] = useState(contextValue);
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    setInputValue(contextValue);
    setDebouncedValue(contextValue);
  }, [contextValue]);

  useEffect(() => {
    const trimmed = inputValue.trim();
    if (trimmed === debouncedValue) return undefined;

    const timer = setTimeout(() => {
      setDebouncedValue(trimmed);
      isInternalUpdate.current = true;

      if (trimmed) {
        setFilter(queryKey, trimmed);
      } else {
        removeFilter(queryKey);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue, debouncedValue, queryKey, setFilter, removeFilter]);

  const onChange = useCallback((value: string) => {
    setInputValue(value);
  }, []);

  return { inputValue, debouncedValue, onChange };
};
